import { useState } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { dashboardQuery } from '@/api/queries'
import { StatCard } from '@/components/Charts'
import { OverallStatusBadge, StatusBadge } from '@/components/StatusBadge'
import { ServiceFormModal } from '@/components/ServiceFormModal'
import { Button, Card, EmptyState, Loading } from '@/components/ui'
import { useAuth } from '@/lib/useAuth'
import { formatPercent, formatRelative } from '@/lib/format'

export const Route = createFileRoute('/_app/dashboard')({
  // Data se natahnou uz behem prechodu na cestu, takze stranka nenaskoci
  // prazdna a pak se neprekresli — obnovovani pak resi refetchInterval.
  loader: ({ context }) => context.queryClient.ensureQueryData(dashboardQuery()),
  component: DashboardPage,
})

function DashboardPage() {
  const { data, isLoading } = useQuery(dashboardQuery())
  const { user } = useAuth()
  const [creating, setCreating] = useState(false)

  if (isLoading || !data) return <Loading />

  const isAdmin = user?.role === 'ADMIN'

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Prehled</h1>
          <p className="text-sm text-slate-500">
            Aktualizovano {formatRelative(data.generatedAt)} · obnovuje se automaticky
          </p>
        </div>
        {isAdmin && <Button onClick={() => setCreating(true)}>Nova sluzba</Button>}
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard label="Sluzby" value={String(data.serviceCount)} />
        <StatCard label="Cile" value={String(data.targetCount)} hint={`${data.unknownCount} bez dat`} />
        <StatCard
          label="Bezi"
          value={String(data.upCount)}
          tone={data.upCount > 0 ? 'good' : 'default'}
        />
        <StatCard
          label="Vypadky"
          value={String(data.downCount)}
          tone={data.downCount > 0 ? 'bad' : 'default'}
        />
      </div>

      <Card>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
              Prumerna dostupnost za 24 h
            </p>
            <p className="mt-1 text-3xl font-semibold tabular-nums text-slate-900">
              {formatPercent(data.uptime24h)}
            </p>
          </div>
        </div>
      </Card>

      {data.services.length === 0 ? (
        <EmptyState
          title="Zatim tu neni zadna sluzba"
          hint={isAdmin ? 'Zaloz sluzbu a pridej do ni prvni URL ke hlidani.' : 'Pozadej admina o zalozeni sluzby.'}
        />
      ) : (
        <div className="space-y-3">
          {data.services.map((service) => (
            <Card key={service.id} className="hover:border-brand-500/40">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <Link
                      to="/services/$serviceId"
                      params={{ serviceId: service.id }}
                      className="truncate font-medium text-slate-900 hover:text-brand-700"
                    >
                      {service.name}
                    </Link>
                    <OverallStatusBadge status={service.overallStatus} />
                    {service.isPublic && (
                      <Link
                        to="/status/$publicId"
                        params={{ publicId: service.publicId }}
                        className="rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700 hover:bg-brand-100"
                      >
                        verejna stranka
                      </Link>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-slate-500">
                    {service.targetCount} cilu · {service.upCount} bezi · {service.downCount} mimo provoz
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs uppercase tracking-wide text-slate-400">24h SLA</p>
                  <p className="text-lg font-semibold tabular-nums text-slate-900">
                    {formatPercent(service.uptime24h)}
                  </p>
                </div>
              </div>

              {/* Nefunkcni cile ukazujeme rovnou — to je jediny duvod, proc se
                  na tuhle obrazovku nekdo dival ve chvili, kdy neco hori. */}
              {service.downTargets.length > 0 && (
                <ul className="mt-3 space-y-2 border-t border-slate-100 pt-3">
                  {service.downTargets.map((target) => (
                    <li key={target.id} className="flex flex-wrap items-center gap-2 text-sm">
                      <StatusBadge status={target.currentStatus} />
                      <Link
                        to="/services/$serviceId/targets/$targetId"
                        params={{ serviceId: service.id, targetId: target.id }}
                        className="font-medium text-slate-700 hover:text-brand-700"
                      >
                        {target.name}
                      </Link>
                      <span className="truncate text-slate-400">{target.lastErrorMessage ?? target.url}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          ))}
        </div>
      )}

      {creating && <ServiceFormModal onClose={() => setCreating(false)} />}
    </div>
  )
}
